<template>
  <div class="modal fade" id="expenseCategoryModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow expense-category-modal">
        <div class="modal-header border-0 pb-0 align-items-start">
          <div>
            <div class="modal-badge mb-2">
              <i class="ri-price-tag-3-line me-2"></i>
              {{ isEditMode ? $t("expense_category_page.update_category_badge") : $t("expense_category_page.new_category_badge") }}
            </div>
            <h5 class="modal-title fw-bold text-title mb-1">
              {{ isEditMode ? $t("expense_category_page.edit_expense_category") : $t("expense_category_page.create_expense_category") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("expense_category_page.modal_description") }}
            </p>
          </div>

          <button
            type="button"
            class="btn-close shadow-none mt-1"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          ></button>
        </div>

        <div class="modal-body pt-4">
          <form @submit.prevent="$emit('submit')">
            <div class="row g-3">
              <div class="col-md-5">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("expense_category_page.code") }}</label>
                  <span class="field-caption">{{ $t("expense_category_page.short_unique_code") }}</span>
                </div>
                <input
                  :value="form.code"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('expense_category_page.code_placeholder')"
                  @input="onTextInput('code', $event)"
                />
                <button
                  v-if="!isEditMode"
                  type="button"
                  class="btn btn-light generate-code-btn mt-3"
                  @click="$emit('generate-code')"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("expense_category_page.generate_code") }}
                </button>
              </div>

              <div class="col-md-7">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("expense_category_page.category_name") }}</label>
                  <span class="field-caption">{{ $t("expense_category_page.main_display_name") }}</span>
                </div>
                <input
                  :value="form.name"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('expense_category_page.category_name_placeholder')"
                  @input="onTextInput('name', $event)"
                />
              </div>

              <div class="col-12">
                <label class="form-label fw-semibold text-title">{{ $t("expense_category_page.description") }}</label>
                <textarea
                  :value="form.description"
                  rows="5"
                  class="form-control modal-input modal-textarea"
                  :placeholder="$t('expense_category_page.description_placeholder')"
                  @input="onTextInput('description', $event)"
                ></textarea>
                <div class="helper-text">{{ $t("expense_category_page.helper_text") }}</div>
              </div>
            </div>

            <div class="modal-footer border-0 px-0 pb-0 pt-4 justify-content-between">
              <button type="button" class="btn btn-light px-4 h-50" data-bs-dismiss="modal">
                {{ $t("common.cancel") }}
              </button>

              <button type="submit" class="btn style-one px-4 h-50 modal-submit-btn" :disabled="loading">
                <i class="ri-save-line me-1"></i>
                {{ loading ? $t("expense_category_page.saving") : isEditMode ? $t("expense_category_page.update_category") : $t("expense_category_page.create_category") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

type ExpenseCategoryForm = {
  code: string;
  name: string;
  description: string;
};

export default defineComponent({
  name: "CreateCategoryModal",
  props: {
    form: {
      type: Object as PropType<ExpenseCategoryForm>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update-field", "generate-code", "submit"],
  setup(_, { emit }) {
    const onTextInput = (field: keyof ExpenseCategoryForm, event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
      emit("update-field", field, target?.value ?? "");
    };

    return {
      onTextInput,
    };
  },
});
</script>

<style scoped>
.expense-category-modal {
  border-radius: 20px;
  padding: 10px;
}

.modal-badge {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4d5ee1;
  font-size: 12px;
  font-weight: 700;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  font-size: 12px;
  color: #98a2b3;
}

.modal-input {
  min-height: 52px;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  background: #fbfcfe;
  color: #1f2940;
  box-shadow: none;
  padding: 12px 16px;
}

.modal-input:focus {
  border-color: #5d5fef;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
}

.helper-text {
  margin-top: 8px;
  font-size: 12px;
  color: #98a2b3;
}

.generate-code-btn {
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  min-height: 44px;
  padding-inline: 16px;
}

.modal-submit-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}
</style>
